import { UnreachableCaseError } from "../../lib/lang"
import { Logger } from "../../lib/logging"
import { ApiError, AppError, DisplayError, ErrorDisplay, ErrorMatcher } from "../../core/api/errorTypes"

const NUMBER_REGEX = /^\d+$/
const WILDCARD_REGEX = /x|X/

function matchErrorCode(expected: string, actual: string) {
    if (expected.startsWith("!")) {
        return actual !== expected
    }

    return actual === expected
}

function matchResponseStatus(expected: string | number, actual: number | undefined) {
    if (actual === undefined) {
        return false
    }

    if (typeof expected === "number") {
        return actual === expected
    }

    const actualStr = `${actual}`

    for (let i = 0; i < 3; i++) {
        if (expected[i] === "x" || expected[i] === "X") {
            continue
        }
        if (actualStr[i] !== expected[i]) {
            return false
        }
    }

    return true
}

function matchApiStatus(expected: string, actual: string | undefined) {
    if (actual === undefined) {
        return false
    }

    if (expected.startsWith("!")) {
        return actual !== expected
    }

    return actual === expected
}

export function matches(errorMatcher: ErrorMatcher, error: AppError) {
    switch (errorMatcher.type) {
        case "error-code":
            return matchErrorCode(errorMatcher.match, error.code)
        case "response-status":
            return (error instanceof ApiError) && matchResponseStatus(errorMatcher.match, error.status)
        case "api-status":
            return (error instanceof ApiError) && matchApiStatus(errorMatcher.match, error.apiStatus)
        default:
            throw new UnreachableCaseError(errorMatcher)
    }
}

function countWildcards(responseStatus: string) {
    let i = 0
    for (const _match of responseStatus.matchAll(WILDCARD_REGEX)) {
        i++
    }
    return i
}

function rankByMatcherType(a: ErrorMatcher, b: ErrorMatcher) {
    if (a.type === b.type) {
        return 0
    }

    if (a.type === "error-code") {
        // Error code is the least specific type
        return 1
    } else if (b.type === "error-code") {
        return -1
    }

    if (a.type === "response-status") {
        return 1
    } else if (b.type === "response-status") {
        return -1
    }

    // Cannot get here: both types would have to be api-status
    // but then the first test above would have matched
    return 0
}

function rankErrorCodeOrApiStatus(a: string, b: string) {
    if (a.startsWith("!")) {
        if (b.startsWith("!")) {
            // Both are negated, just order lexicographically
            return a.localeCompare(b)
        } else {
            // a is a negation so b is more specific
            return 1
        }
    } else if (b.startsWith("!")) {
        // b is a negation so a is more specific
        return -1
    } else {
        // Neither a nor b are negations so order lexicographically
        return a.localeCompare(b)
    }
}

function rankResponseStatus(a: string | number, b: string | number) {
    const aStr = typeof a === "string" ? a : `${a}`
    const bStr = typeof b === "string" ? b : `${b}`

    return aStr.localeCompare(bStr)
    /*
    if (typeof a === "number") {
        if (typeof b === "number") {
            // Order doesn't matter when no wildcards are present so just order by value
            return b - a
        } else if (NUMBER_REGEX.test(b)) {
            // Here b is a pure number but inside a string; handle like above
            return parseInt(b) - a
        } else {
            // If we get here, b contains a wildcard and a does not
            // -> a is more specific
            return -1
        }
    } else if (typeof b === "number") {
        // We know that a is not a number if we get here
        if (NUMBER_REGEX.test(a)) {
            // ... but it could still be a number inside a string
            return b - parseInt(a)
        } else {
            // If we get here, a contains a wildcard and b does not
            // -> b is more specific
            return 1
        }
    } else {
        // If we get here both a and b contain wildcards
        const aWild = countWildcards(a)
        const bWild = countWildcards(b)

        if (aWild !== bWild) {
            return bWild - aWild
        }
    }
    */
}

export function orderBySpecificity(errorMatchers: ErrorMatcher[]) {
    return [...errorMatchers].sort((a, b) => {
        if (a.type !== b.type) return rankByMatcherType(a, b)

        // At this point we know that a.type === b.type
        if ((a.type === "error-code" && b.type === "error-code") || (a.type === "api-status" && b.type === "api-status")) {
            return rankErrorCodeOrApiStatus(a.match, b.match)
        } else if (a.type === "response-status" && b.type === "response-status") {
            return rankResponseStatus(a.match, b.match)
        }

        // Cannot get here
        return 0
    })
}

function isNegated(errorMatcher: ErrorMatcher) {
    if (errorMatcher.type === "response-status") return false
    return errorMatcher.match.startsWith("!")
}

function hasWildcard(errorMatcher: ErrorMatcher) {
    if (errorMatcher.type !== "response-status") return false
    if (typeof errorMatcher.match === "number") return false
    return WILDCARD_REGEX.test(errorMatcher.match)
}

export function describeMatcher(errorMatcher: ErrorMatcher) {
    const relation = isNegated(errorMatcher) ? "!=" : hasWildcard(errorMatcher) ? "~=" : "=="
    return `match[${errorMatcher.type} ${relation} ${errorMatcher.match}]`
}

export function describeError(error: AppError) {
    const apiError = error instanceof ApiError ? error : undefined
    return `error[code: ${error.code}, HTTP status: ${apiError?.status}, API status: ${apiError?.apiStatus}]`
}

export type MatchOptions = Readonly<{
    errorDisplay?: ErrorDisplay
    errorMatchers?: ErrorMatcher[]
    errorMessagePrefix?: string
}>

export type ErrorMatch = Readonly<{
    displayError: DisplayError
    matcher?: ErrorMatcher
}>

export function applyMatchers(
    error: unknown,
    { errorDisplay = ErrorDisplay.SNACKBAR, errorMatchers = [], errorMessagePrefix = "" }: MatchOptions,
): ErrorMatch {
    const logger = new Logger("error-match")

    const matchersToUse = orderBySpecificity(errorMatchers)

    const appError = AppError.wrap(error)
    let errorMatcher: ErrorMatcher | undefined = undefined

    logger.info(`Matching error ${describeError(appError)} against:`)
    for (const matcher of matchersToUse) {
        logger.info(`    matcher: ${describeMatcher(matcher)}`)
        if (matches(matcher, appError)) {
            errorMatcher = matcher
            logger.info(`      ↳ MATCH`)
            break
        }
    }

    const errorDisplayToUse = errorMatcher?.display ?? errorDisplay
    let errorMessageToUse = errorMatcher?.message ?? appError.displayMessage
    errorMessageToUse = errorMessagePrefix ? `${errorMessagePrefix}. ${errorMessageToUse}` : errorMessageToUse

    logger.info(
        `Effective error values [matchers: ${matchersToUse.length}, display: ${errorDisplayToUse}, message: ${errorMessageToUse}]`,
    )

    const displayError: DisplayError = {
        display: errorDisplayToUse,
        error: {
            ...appError.toObject(),
            displayMessage: errorMessageToUse,
        },
    }

    return {
        displayError: displayError,
        matcher: errorMatcher,
    }
}
